import { useEffect, useMemo, useState } from "react";
import { Save, RotateCcw, ShieldAlert, Pencil } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTours } from "../lib/useTours";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";

const EDITABLE_FIELDS = [
  { key: "title", label: "Title" },
  { key: "tag", label: "Tag" },
  { key: "location", label: "Location" },
  { key: "image", label: "Image URL" },
  { key: "price", label: "Price (₹)", type: "number" },
  { key: "duration", label: "Duration" },
  { key: "dates", label: "Dates / departures" },
  { key: "short", label: "Short description", textarea: true },
];

export default function Admin() {
  const { user, loading, login } = useAuth();
  const [isAdmin, setIsAdmin] = useState(null);
  const { tours } = useTours();
  const { toast } = useToast();
  const [editing, setEditing] = useState(null); // tour object being edited
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [overrides, setOverrides] = useState([]);

  const reloadOverrides = async () => {
    try {
      const { data } = await api.get("/admin/tours");
      setOverrides(data);
    } catch {}
  };

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setIsAdmin(false);
      return;
    }
    (async () => {
      try {
        await api.get("/admin/whoami");
        setIsAdmin(true);
        reloadOverrides();
      } catch {
        setIsAdmin(false);
      }
    })();
  }, [user, loading]);

  const overrideMap = useMemo(() => Object.fromEntries(overrides.map((o) => [o.id, o])), [overrides]);

  const startEdit = (tour) => {
    setEditing(tour);
    setDraft({
      title: tour.title,
      tag: tour.tag,
      location: tour.location,
      image: tour.image,
      price: tour.price,
      duration: tour.duration,
      dates: tour.dates || "",
      short: tour.short,
    });
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const patch = { ...draft, price: draft.price ? parseInt(draft.price, 10) : undefined };
      await api.put(`/admin/tours/${editing.id}`, patch);
      toast({ title: "Saved", description: `${editing.title} updated.` });
      setEditing(null);
      await reloadOverrides();
      // Reload public tours (soft refresh)
      window.location.reload();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const resetTour = async (tour) => {
    if (!window.confirm(`Reset ${tour.title} to defaults?`)) return;
    try {
      await api.delete(`/admin/tours/${tour.id}`);
      toast({ title: "Reset", description: `${tour.title} restored.` });
      await reloadOverrides();
      window.location.reload();
    } catch {
      toast({ title: "Reset failed", variant: "destructive" });
    }
  };

  if (loading || isAdmin === null) {
    return (
      <main className="pt-32 pb-24 min-h-screen text-center">
        <div className="w-10 h-10 rounded-full border-2 border-[#1F4D3A]/20 border-t-[#1F4D3A] animate-spin mx-auto" />
      </main>
    );
  }

  if (!user) {
    return (
      <main className="pt-32 pb-24 min-h-screen paper-texture text-center px-6">
        <ShieldAlert className="w-10 h-10 text-[#C87A50] mx-auto mb-4" />
        <h1 className="font-serif text-4xl text-[#1F4D3A] mb-3">Sign in to manage tours</h1>
        <Button onClick={login} data-testid="admin-login-btn" className="btn-primary bg-[#1F4D3A] hover:bg-[#2D6B4F] text-white rounded-full h-12 px-8">
          Sign in with Google
        </Button>
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="pt-32 pb-24 min-h-screen paper-texture text-center px-6" data-testid="admin-forbidden">
        <ShieldAlert className="w-10 h-10 text-[#C87A50] mx-auto mb-4" />
        <h1 className="font-serif text-4xl text-[#1F4D3A] mb-3">Not authorised.</h1>
        <p className="text-[#5A4A38]">This section is reserved for the On Wheels studio.</p>
      </main>
    );
  }

  return (
    <main className="pt-24 sm:pt-32 pb-16 paper-texture min-h-screen">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <span className="leaf-divider inline-flex">Studio</span>
          <h1 className="font-serif text-3xl sm:text-5xl font-bold text-[#1F4D3A] mt-3">Tour editor</h1>
          <p className="text-[#4A3728] mt-3 max-w-2xl">
            Change title, image, price, duration, dates and description of any tour. Changes go live instantly for every visitor.
          </p>
        </div>

        <div className="bg-white border border-[#E5DDCB] rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F0E8] text-[#1F4D3A]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Tour</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Location</th>
                <th className="text-left px-4 py-3 font-semibold">Price</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Duration</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="admin-tour-table">
              {tours.map((t) => (
                <tr key={t.id} className="border-t border-[#E5DDCB]" data-testid={`admin-row-${t.id}`}>
                  <td className="px-4 py-3 text-[#1F4D3A]">
                    <div className="flex items-center gap-3">
                      <img src={t.image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                      <div>
                        <div className="font-semibold">{t.title}</div>
                        <div className="text-xs text-[#6B8E5A] uppercase tracking-widest">{t.tag}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#5A4A38] hidden md:table-cell">{t.location}</td>
                  <td className="px-4 py-3 text-[#1F4D3A] font-semibold">₹{Number(t.price).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-[#5A4A38] hidden md:table-cell">{t.duration}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" onClick={() => startEdit(t)} data-testid={`admin-edit-${t.id}`} className="bg-[#1F4D3A] hover:bg-[#2D6B4F] text-white rounded-full h-8 px-3 mr-2">
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    {overrideMap[t.id] && (
                      <Button size="sm" variant="outline" onClick={() => resetTour(t)} data-testid={`admin-reset-${t.id}`} className="rounded-full h-8 px-3">
                        <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !saving && setEditing(null)} data-testid="admin-edit-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-[#E5DDCB] flex items-center justify-between">
              <h2 className="font-serif text-2xl font-bold text-[#1F4D3A]">Edit — {editing.title}</h2>
              <button onClick={() => setEditing(null)} className="text-[#6B8E5A] hover:text-[#1F4D3A]" aria-label="Close">✕</button>
            </div>
            <div className="p-6 space-y-4">
              {EDITABLE_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="block text-xs uppercase tracking-widest text-[#6B8E5A] font-semibold mb-2">{f.label}</label>
                  {f.textarea ? (
                    <textarea
                      data-testid={`admin-field-${f.key}`}
                      rows={3}
                      value={draft[f.key] ?? ""}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-[#F7F3EA] border border-[#E5DDCB] text-sm focus:outline-none focus:border-[#1F4D3A]"
                    />
                  ) : (
                    <input
                      data-testid={`admin-field-${f.key}`}
                      type={f.type || "text"}
                      value={draft[f.key] ?? ""}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-[#F7F3EA] border border-[#E5DDCB] text-sm focus:outline-none focus:border-[#1F4D3A]"
                    />
                  )}
                </div>
              ))}
              {draft.image && (
                <div>
                  <span className="text-xs uppercase tracking-widest text-[#6B8E5A] font-semibold">Preview</span>
                  <img src={draft.image} alt="" className="mt-2 w-full max-h-48 object-cover rounded-lg border border-[#E5DDCB]" />
                </div>
              )}
            </div>
            <div className="p-6 border-t border-[#E5DDCB] flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditing(null)} disabled={saving} className="rounded-full">Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="admin-save-btn" className="btn-primary bg-[#1F4D3A] hover:bg-[#2D6B4F] text-white rounded-full">
                {saving ? "Saving…" : (<><Save className="w-4 h-4 mr-2" /> Save changes</>)}
              </Button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
